import type { ActionRequiredIssue } from 'core/components/ActionRequiredModalTrigger'
import LearnMoreLink from 'core/components/LearnMoreLink'
import Text from 'core/elements/Text'

/**
 * Checks for required actions related to the Image Library endpoint
 * @param needToValidateCert - Whether certificate validation is needed
 * @param isBrowserLocalNetworkAccessAllowed - Whether browser allows local network access
 * @param glanceHostNotReachable - Whether the glance host is not reachable
 * @param hostEndpoint - The host endpoint URL for certificate trust
 * @param showCertificateExpiryWarning - Whether to also surface a nearing-expiry certificate
 * @param certificateExpiryMessage - Message shown for the nearing-expiry certificate issue
 * @returns Array of action required issues
 */
export const checkImageLibraryRequiredActions = ({
  needToValidateCert,
  isBrowserLocalNetworkAccessAllowed,
  glanceHostNotReachable,
  hostEndpoint,
  showCertificateExpiryWarning = false,
  certificateExpiryMessage = 'Image library certificate is nearing expiry',
}: {
  needToValidateCert: boolean
  isBrowserLocalNetworkAccessAllowed: boolean
  glanceHostNotReachable: boolean
  hostEndpoint?: string
  // PCD-9693: adds a "Certificate Nearing Expiry" line item to the Needs Action modal.
  showCertificateExpiryWarning?: boolean
  certificateExpiryMessage?: string
}): ActionRequiredIssue[] => {
  // CILRA-FR-001: no endpoint means nothing is actionable yet
  if (!hostEndpoint) {
    return []
  }
  const issues: ActionRequiredIssue[] = []

  // CILRA-FR-002: certificate-nearing-expiry warning always sorts first (PCD-9693)
  if (showCertificateExpiryWarning) {
    issues.push({
      key: 'certificateExpiry',
      title: 'Certificate Nearing Expiry',
      description: <Text variant="body2">{certificateExpiryMessage}</Text>,
      priority: 0,
      severity: 'warning',
      source: 'images',
    })
  }

  // CILRA-FR-003: untrusted certificate
  if (needToValidateCert) {
    issues.push({
      key: 'trustCert',
      title: 'Trust Image Library Certificate',
      description: (
        <Text variant="body2">
          Make sure you have accepted the certificate for this image library endpoint.{' '}
          {hostEndpoint ? (
            <LearnMoreLink text="Trust Image Library Certificate" url={hostEndpoint} />
          ) : null}
        </Text>
      ),
      priority: 1,
      severity: 'warning',
      source: 'images',
    })
  }

  // CILRA-FR-004: local network access blocked
  if ((needToValidateCert || glanceHostNotReachable) && !isBrowserLocalNetworkAccessAllowed) {
    issues.push({
      key: 'localNetworkAccess',
      title: 'Allow Local Network Access',
      description: (
        <Text variant="body2">
          Make sure you have allowed Local Network Access in your browser so this page can connect
          to the Image Library endpoint.{' '}
          {/* <LearnMoreLink text={'Learn more...'} url={troubleshootImageLibraryDoc} /> */}
        </Text>
      ),
      priority: 2,
      severity: 'warning',
      source: 'images',
    })
  }

  // CILRA-FR-005: host unreachable
  if (glanceHostNotReachable) {
    issues.push({
      key: 'hostNotReachable',
      title: 'Image Library Host Not Reachable',
      description: (
        <Text variant="body2">
          Make sure the image library service is accessible from the network you are on.{' '}
          {/* <LearnMoreLink text={'Learn more...'} url={troubleshootImageLibraryDoc} /> */}
        </Text>
      ),
      priority: 3,
      severity: 'warning',
      source: 'images',
    })
  }

  return issues
}
